import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Layout } from "../../components/Layout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { useTheme } from "../../contexts/ThemeContext";
import { useToast } from "../../hooks/use-toast";
import { setLocale } from "../../features/auth/languageSlice";
import { fetchEmployeeDataAPI } from "./settingsAPI";
import { FormattedMessage, useIntl } from "react-intl";
import ReactSelect from "react-select";
import { Skeleton } from "../../components/ui/skeleton";

// Helper function to map roleId to a display name
const getRoleName = (roleId) => {
  if (roleId === 5) {
    return "Technician";
  }
  // This can be expanded for other role IDs in the future
  return "Unknown Role";
};

function Settings({ onNavigate }) {
  const dispatch = useDispatch();
  const { isDarkMode, setTheme } = useTheme();
  const { toast } = useToast();
  const intl = useIntl();

  const [isFr, setFr] = useState(localStorage.getItem("locale") === "fr");

  const { profile, loading } = useSelector((state) => state.settings);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const employeeId = user?.employeeid;
    if (employeeId) {
      dispatch(fetchEmployeeDataAPI(employeeId));
    }
  }, [dispatch]);

  const handleThemeToggle = (checked) => {
    setTheme(checked);
    localStorage.setItem("darkMode", checked ? "true" : "false");
  };

  const handleLanguageToggle = (checked) => {
    const newLocale = checked ? "fr" : "en";
    setFr(checked);
    dispatch(setLocale(newLocale));
    localStorage.setItem("locale", newLocale);
    toast({
      title: intl.formatMessage({
        id:
          newLocale === "fr"
            ? "settings.LanguageChanged.fr"
            : "settings.LanguageChanged.en",
        defaultMessage: "Language Changed",
      }),
      description: intl.formatMessage({
        id:
          newLocale === "fr"
            ? "settings.LanguageSetT.fr"
            : "settings.LanguageSetT.en",
        defaultMessage: `Language set to ${
          newLocale === "fr" ? "French" : "English"
        }.`,
      }),
    });
  };

  const skillsOptions = [
    { value: "Plumbing", label: "Plumbing" },
    { value: "HVAC", label: "HVAC" },
    { value: "Electrical", label: "Electrical" },
    { value: "Appliance Repair", label: "Appliance Repair" },
    { value: "Solar systems", label: "Solar systems" },
    { value: "Water systems", label: "Water systems" },
    { value: "Wiring", label: "Wiring" },
    { value: "General Maintenance", label: "General Maintenance" },
  ];

  const ProfileSkeleton = () => (
    <Card>
      <CardHeader>
        <CardTitle>
          <Skeleton className="h-6 w-1/3" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 !pt-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
          <div className="md:col-span-2">
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout
      title={
        <FormattedMessage id="settings.pageTitle" defaultMessage="Settings" />
      }
      onNavigate={onNavigate}
      //  onRefresh={handleRefresh}
    >
      <div className="space-y-6">
        {loading && !profile ? (
          <ProfileSkeleton />
        ) : (
          profile && (
            <Card>
              <CardHeader className="!p-4">
                <CardTitle className="text-xl">
                  <FormattedMessage
                    id="settings.profileTitle"
                    defaultMessage="Technician Profile"
                  />
                </CardTitle>
              </CardHeader>
              <CardContent className="!p-4 space-y-4 !pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.firstName"
                        defaultMessage="First Name"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {profile.firstName || "-"}
                    </p>
                  </div>

                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.lastName"
                        defaultMessage="Last Name"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {profile.lastName || "-"}
                    </p>
                  </div>

                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.dob"
                        defaultMessage="Date of Birth"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {profile.birthDate || "-"}
                    </p>
                  </div>

                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.employeeId"
                        defaultMessage="Employee ID"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {profile.employeeId || "-"}
                    </p>
                  </div>

                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.phone"
                        defaultMessage="Phone Number"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {`${profile.countryCode || ""} ${
                        profile.phone || ""
                      }`.trim() || "-"}
                    </p>
                  </div>

                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.gender"
                        defaultMessage="Gender"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {profile.gender
                        ? profile.gender.charAt(0).toUpperCase() +
                          profile.gender.slice(1)
                        : "-"}
                    </p>
                  </div>

                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.role"
                        defaultMessage="Access Role"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {getRoleName(profile.roleId) || "-"}
                    </p>
                  </div>

                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.status"
                        defaultMessage="Status"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {profile.isActive ? "Active" : "Inactive"}
                    </p>
                  </div>

                  {/* Full-width fields */}
                  <div className="col-span-2">
                    <Label>
                      <FormattedMessage
                        id="settings.email"
                        defaultMessage="Email"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {profile.email || "-"}
                    </p>
                  </div>

                  <div className="col-span-2">
                    <Label>
                      <FormattedMessage
                        id="settings.location"
                        defaultMessage="Location"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {profile.location || "-"}
                    </p>
                  </div>

                  <div className="col-span-2">
                    <Label>
                      <FormattedMessage
                        id="settings.skills"
                        defaultMessage="Skills"
                      />
                    </Label>
                    <p className="text-sm text-gray-900 dark:text-gray-500">
                      {profile.skills?.length
                        ? skillsOptions
                            .filter((opt) => profile.skills.includes(opt.value))
                            .map((opt) => opt.label)
                            .join(", ")
                        : "-"}
                    </p>
                  </div>
                </div>
              </CardContent>

              {/* <CardContent className="space-y-4 !pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.firstName"
                        defaultMessage="First Name"
                      />
                    </Label>
                    <Input value={profile.firstName || ""} disabled />
                  </div>
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.lastName"
                        defaultMessage="Last Name"
                      />
                    </Label>
                    <Input value={profile.lastName || ""} disabled />
                  </div>
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.dob"
                        defaultMessage="Date of Birth"
                      />
                    </Label>
                    <Input
                      type="date"
                      value={profile.birthDate || ""}
                      disabled
                    />
                  </div>
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.employeeId"
                        defaultMessage="Employee ID"
                      />
                    </Label>
                    <Input value={profile.employeeId || ""} disabled />
                  </div>
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.phone"
                        defaultMessage="Phone Number"
                      />
                    </Label>
                    <Input
                      value={`${profile.countryCode || ""} ${
                        profile.phone || ""
                      }`.trim()}
                      disabled
                    />
                  </div>
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.email"
                        defaultMessage="Email"
                      />
                    </Label>
                    <Input type="email" value={profile.email || ""} disabled />
                  </div>
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.gender"
                        defaultMessage="Gender"
                      />
                    </Label>
                    <Input
                      value={
                        profile.gender
                          ? profile.gender.charAt(0).toUpperCase() +
                            profile.gender.slice(1)
                          : ""
                      }
                      disabled
                    />
                  </div>
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.role"
                        defaultMessage="Access Role"
                      />
                    </Label>
                    <Input value={getRoleName(profile.roleId)} disabled />
                  </div>
                  <div className="md:col-span-2">
                    <Label>
                      <FormattedMessage
                        id="settings.skills"
                        defaultMessage="Skills"
                      />
                    </Label>
                    <ReactSelect
                      isMulti
                      name="skills"
                      options={skillsOptions}
                      className="basic-multi-select mt-1"
                      classNamePrefix="select"
                      value={skillsOptions.filter((opt) =>
                        profile.skills.includes(opt.value)
                      )}
                      isDisabled
                    />
                  </div>
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.location"
                        defaultMessage="Location"
                      />
                    </Label>
                    <Input value={profile.location || ""} disabled />
                  </div>
                  <div>
                    <Label>
                      <FormattedMessage
                        id="settings.status"
                        defaultMessage="Status"
                      />
                    </Label>
                    <Input
                      value={profile.isActive ? "Active" : "Inactive"}
                      disabled
                    />
                  </div>
                </div>
              </CardContent> */}
            </Card>
          )
        )}

        <Card>
          <CardHeader className="!p-4">
            <CardTitle className="text-xl">
              <FormattedMessage
                id="settings.preferencesTitle"
                defaultMessage="Preferences"
              />
            </CardTitle>
          </CardHeader>
          <CardContent className="!p-4 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium">
                  <FormattedMessage
                    id="settings.darkMode"
                    defaultMessage="Dark Mode"
                  />
                </h4>
                <p className="text-sm text-gray-500">
                  <FormattedMessage
                    id="settings.darkModeDescription"
                    defaultMessage="Use dark theme interface"
                  />
                </p>
              </div>
              <Switch
                checked={isDarkMode}
                onCheckedChange={handleThemeToggle}
              />
            </div>
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium">
                  <FormattedMessage
                    id="settings.languageToggle"
                    defaultMessage="Language"
                  />
                </h4>
                <p className="text-sm text-gray-500">
                  <FormattedMessage
                    id="settings.languageToggleDescription"
                    defaultMessage="Switch between English and French"
                  />
                </p>
              </div>
              <Switch checked={isFr} onCheckedChange={handleLanguageToggle} />
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

export default Settings;
